import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { instance } from "api/apiBase";
import { getErrorMessage } from "utils/error";
import { Button, ButtonVariant, Modal } from "shared/components";
import type { IdentityTheftProtectionRow } from "../types";

type Props = {
  target: IdentityTheftProtectionRow | null;
  onClose: () => void;
};

export const ResendInviteModal = ({ target, onClose }: Props) => {
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: (enrollmentId: string) =>
      instance.post(
        `portal/v1/identity_monitoring_enrollments/${enrollmentId}/resend_invite`
      ),
    onSuccess: () => {
      toast.success("Invite resent successfully");
      queryClient.invalidateQueries({
        queryKey: ["IDENTITY_MONITORING_ENROLLMENTS"]
      });
      onClose();
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  const handleResend = () => {
    if (target) mutate(target.id);
  };

  return (
    <Modal
      opened={target !== null}
      onClose={onClose}
      panelClassName="p-6 max-w-[440px] w-full"
    >
      <h2 className="text-content-1000 font-bold text-xl mb-2 text-center">
        Resend invite?
      </h2>
      <p className="text-content-500 text-base text-center">
        A new invitation will be sent to{" "}
        <span className="font-semibold text-content-1000">{target?.name}</span>{" "}
        <span className="font-semibold text-content-1000">
          ({target?.email})
        </span>
        .
      </p>
      <div className="border-b border-alpha-40 my-6" />
      <div className="flex gap-2">
        <div className="w-full">
          <Button className="w-full" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
        </div>
        <div className="w-full">
          <Button
            variant={ButtonVariant.PRIMARY}
            className="w-full"
            onClick={handleResend}
            loading={isPending}
          >
            Resend invite
          </Button>
        </div>
      </div>
    </Modal>
  );
};
